import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { triggerHaptic } from '../../lib/haptics';

// --- Pull-to-refresh tuning ----------------------------------------------
// Damped pull distance at which the gesture "arms" (release now → refresh).
const THRESHOLD = 64;
// The pull asymptotes toward this, so it never runs away from the finger.
const MAX_PULL = 110;
// Where the content rests while the refresh promise is in flight.
const REFRESH_REST = 54;
// How far the finger must travel before we commit to a vertical pull.
const DIRECTION_LOCK_PX = 8;
// Settle/return easing duration.
const SETTLE_MS = 320;
// Radius of the indicator arc (keep in sync with the <circle r>).
const ARC_R = 10;
const ARC_C = 2 * Math.PI * ARC_R;

const reduceMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/**
 * Native-style pull-to-refresh for the app's main scroll surface.
 *
 * Owns the scroll container itself (so it keeps the momentum-scroll + safe-area
 * setup AppShell gives it) and, when the user drags down from the very top,
 * tracks the finger with rubber-band resistance, winds a small arc up to a ring,
 * ticks a haptic when it arms, then — on release past the threshold — holds a
 * spinner while `onRefresh` runs and springs back when it resolves. All driven
 * by direct style writes (no per-frame React renders) for 60fps. Honours Reduce
 * Motion (refreshes with no theatre) and no-ops on non-touch / when disabled or
 * inside a `[data-no-pull]` region.
 */
export function PullToRefresh({
  className,
  onRefresh,
  enabled = true,
  containerRef,
  children
}: {
  className?: string;
  onRefresh: () => Promise<unknown> | unknown;
  enabled?: boolean;
  /** Optional external ref to the scroll element (e.g. for scroll restoration). */
  containerRef?: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}) {
  const ownRef = useRef<HTMLDivElement>(null);
  const scrollRef = containerRef ?? ownRef;
  const contentRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const spinnerRef = useRef<HTMLDivElement>(null);
  const arcRef = useRef<SVGCircleElement>(null);
  // Keep the latest callback without re-binding the listeners every render.
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;

  useEffect(() => {
    const scroll = scrollRef.current;
    const content = contentRef.current;
    const indicator = indicatorRef.current;
    const spinner = spinnerRef.current;
    const arc = arcRef.current;
    if (!enabled || !scroll || !content || !indicator || !spinner || !arc) return;

    let tracking = false;
    let engaged = false;
    let refreshing = false;
    let armed = false;
    let startX = 0;
    let startY = 0;

    const setContentY = (y: number, animate: boolean) => {
      content.style.transition = animate
        ? `transform ${SETTLE_MS}ms cubic-bezier(0.22,0.61,0.36,1)`
        : 'none';
      content.style.transform = y ? `translateY(${y}px)` : '';
    };

    const renderPull = (dist: number) => {
      const progress = Math.max(0, Math.min(dist / THRESHOLD, 1));
      indicator.style.opacity = String(progress);
      // Wind the arc up to a ~75% ring as you approach the threshold.
      arc.style.strokeDashoffset = String(ARC_C * (1 - progress * 0.75));
      spinner.style.transform = `rotate(${progress * 150}deg)`;
    };

    const settleSpinner = () => {
      spinner.classList.remove('animate-spin', 'text-accent');
      spinner.style.transform = '';
      arc.style.strokeDashoffset = String(ARC_C);
    };

    const reset = () => {
      refreshing = false;
      tracking = false;
      engaged = false;
      armed = false;
      indicator.style.transition = 'opacity 200ms ease';
      indicator.style.opacity = '0';
      setContentY(0, true);
      content.style.willChange = '';
      window.setTimeout(settleSpinner, SETTLE_MS);
    };

    const runRefresh = (withTheatre: boolean) => {
      if (!withTheatre) {
        indicator.style.transition = 'opacity 200ms ease';
        indicator.style.opacity = '0';
        setContentY(0, true);
        content.style.willChange = '';
        void Promise.resolve(refreshRef.current()).catch(() => undefined);
        armed = false;
        return;
      }
      refreshing = true;
      void triggerHaptic('medium', { force: true });
      indicator.style.transition = 'opacity 200ms ease';
      indicator.style.opacity = '1';
      arc.style.strokeDashoffset = String(ARC_C * 0.25); // freeze a 75% ring
      spinner.style.transform = '';
      spinner.classList.add('animate-spin');
      setContentY(REFRESH_REST, true);
      const startedAt = performance.now();
      void Promise.resolve(refreshRef.current())
        .catch(() => undefined)
        .finally(() => {
          // Let the spin read as "did something" even on an instant cache hit.
          const elapsed = performance.now() - startedAt;
          window.setTimeout(reset, Math.max(0, 360 - elapsed));
        });
    };

    const cancel = () => {
      indicator.style.transition = 'opacity 200ms ease';
      indicator.style.opacity = '0';
      setContentY(0, true);
      content.style.willChange = '';
      armed = false;
      window.setTimeout(settleSpinner, SETTLE_MS);
    };

    const onStart = (e: TouchEvent) => {
      if (refreshing || e.touches.length !== 1) return;
      if (scroll.scrollTop > 0) return;
      if ((e.target as Element | null)?.closest?.('[data-no-pull]')) return;
      tracking = true;
      engaged = false;
      armed = false;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const onMove = (e: TouchEvent) => {
      if (!tracking || refreshing) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;

      if (!engaged) {
        // Pulling up, or mostly sideways → it's a scroll / swipe, bow out.
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) {
          tracking = false;
          return;
        }
        if (dy < DIRECTION_LOCK_PX) return;
        if (scroll.scrollTop > 0) {
          tracking = false;
          return;
        }
        engaged = true;
        indicator.style.transition = 'none';
        content.style.willChange = 'transform';
      }

      if (e.cancelable) e.preventDefault();
      const raw = Math.max(0, dy);
      const dist = MAX_PULL * (1 - Math.exp(-raw / MAX_PULL));
      setContentY(dist, false);
      renderPull(dist);

      const nowArmed = dist >= THRESHOLD;
      if (nowArmed !== armed) {
        armed = nowArmed;
        spinner.classList.toggle('text-accent', armed);
        if (armed) void triggerHaptic('light', { force: true });
      }
    };

    const onEnd = () => {
      if (!tracking) return;
      const wasEngaged = engaged;
      tracking = false;
      engaged = false;
      if (!wasEngaged) return;
      if (armed) runRefresh(!reduceMotion());
      else cancel();
    };

    scroll.addEventListener('touchstart', onStart, { passive: true });
    scroll.addEventListener('touchmove', onMove, { passive: false });
    scroll.addEventListener('touchend', onEnd, { passive: true });
    scroll.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      scroll.removeEventListener('touchstart', onStart);
      scroll.removeEventListener('touchmove', onMove);
      scroll.removeEventListener('touchend', onEnd);
      scroll.removeEventListener('touchcancel', onEnd);
      content.style.transition = '';
      content.style.transform = '';
      content.style.willChange = '';
    };
  }, [enabled]);

  return (
    <div ref={scrollRef} className={className}>
      <div
        ref={indicatorRef}
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 z-0 flex justify-center pt-safe"
        style={{ opacity: 0 }}
      >
        <div className="mt-2 flex h-9 w-9 items-center justify-center rounded-full bg-card text-ink shadow-[0_6px_16px_-6px_rgba(10,10,40,0.35)] ring-1 ring-black/[0.04]">
          <div ref={spinnerRef} className="h-5 w-5 text-ink">
            <svg viewBox="0 0 24 24" className="h-5 w-5 -rotate-90">
              <circle
                cx="12"
                cy="12"
                r={ARC_R}
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.14"
                strokeWidth="2.5"
              />
              <circle
                ref={arcRef}
                cx="12"
                cy="12"
                r={ARC_R}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={ARC_C}
                strokeDashoffset={ARC_C}
              />
            </svg>
          </div>
        </div>
      </div>
      <div ref={contentRef} className="relative z-[1] min-h-full">
        {children}
      </div>
    </div>
  );
}
